import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { UserStats, User } from '../../types/user';
import { adminDashboardTabHref } from '../../utils/adminDashboardTabs';
import UserForm from './UserForm';
import DemoUserForm from './DemoUserForm';
import { usePartnerTokenTiers } from '../../contexts/PartnerTokenTiersContext';

interface AdminOverviewProps {
  onRefresh?: () => void;
  stats: UserStats;
  partnerMetrics: {
    pending_insurer_orgs: number;
    active_insurer_orgs: number;
    outstanding_ledger_cents: number;
    credits_granted_mtd: number;
    audit_requests_mtd: number;
    simple_tokens_mtd: number;
    gold_tokens_mtd: number;
    premium_tokens_mtd: number;
  } | null;
}

export const AdminOverview: React.FC<AdminOverviewProps> = ({
  onRefresh,
  stats,
  partnerMetrics,
}) => {
  const navigate = useNavigate();
  const { tokenTitles } = usePartnerTokenTiers();
  const [recentUsers, setRecentUsers] = useState<User[]>([]);
  const [inactiveUser, setInactiveUser] = useState<User | null>(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showDemoUserForm, setShowDemoUserForm] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadExtraOverviewData = useCallback(async () => {
    try {
      const response = await adminService.getUsers({ page: 1, per_page: 20 });
      const users = response.users || [];

      // Find inactive user if any
      const foundInactive = users.find((u) => !u.is_active);
      setInactiveUser(foundInactive || null);

      // Sort recent sign-ins
      const signedIn = users
        .filter((u) => Boolean(u.last_login))
        .sort(
          (a, b) =>
            new Date(b.last_login!).getTime() - new Date(a.last_login!).getTime()
        )
        .slice(0, 4);

      setRecentUsers(signedIn);
    } catch (err) {
      console.error('Failed to load overview user details:', err);
    }
  }, []);

  useEffect(() => {
    loadExtraOverviewData();
  }, [loadExtraOverviewData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (onRefresh) await onRefresh();
      await loadExtraOverviewData();
    } finally {
      setTimeout(() => setIsRefreshing(false), 400);
    }
  };

  const formatLastLogin = (dateStr?: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const outstandingDollars = partnerMetrics
    ? (partnerMetrics.outstanding_ledger_cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    : '$0.00';

  const totalUsers = stats.total_users || 0;
  const activeUsers = stats.active_users || 0;
  const inactiveCount = Math.max(0, totalUsers - activeUsers);
  const lockedUsers = stats.locked_users || 0;
  const adminUsers = stats.admin_users || 0;

  // Segment widths for progress bar
  const activePct = totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0;
  const inactivePct = totalUsers > 0 ? (inactiveCount / totalUsers) * 100 : 0;

  // Needs attention checks
  const needsAttentionItems: Array<{
    id: string;
    text: React.ReactNode;
    actionLabel: string;
    onAction: () => void;
  }> = [];

  if (inactiveUser) {
    needsAttentionItems.push({
      id: 'inactive_user',
      text: (
        <span>
          <strong className="font-semibold">1 user is inactive</strong> —{' '}
          {inactiveUser.email} was never activated.
        </span>
      ),
      actionLabel: 'Review user',
      onAction: () => navigate(adminDashboardTabHref('users')),
    });
  }

  if (partnerMetrics && partnerMetrics.outstanding_ledger_cents > 0) {
    needsAttentionItems.push({
      id: 'outstanding_ledger',
      text: (
        <span>
          <strong className="font-semibold">{outstandingDollars} outstanding</strong> on the
          insurer ledger.
        </span>
      ),
      actionLabel: 'Open ledger',
      onAction: () => navigate(adminDashboardTabHref('insurers')),
    });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Overview</h1>
          <p className="mt-1 text-sm text-slate-500">
            Users, subscriptions and the insurer partner program at a glance.
          </p>
        </div>
        <div className="flex items-center space-x-3 self-start sm:self-auto">
          <span className="text-xs text-slate-400">Updated just now</span>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center space-x-1.5 px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-60"
          >
            <svg
              className={`w-3.5 h-3.5 text-slate-500 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>Refresh stats</span>
          </button>
        </div>
      </div>

      {/* Needs Attention Banner */}
      {needsAttentionItems.length > 0 && (
        <div className="bg-[#FFF1F2] border border-[#FECDD3] rounded-xl p-5 text-slate-900 shadow-sm">
          <div className="flex items-center space-x-2 text-rose-800 font-bold text-xs uppercase tracking-wide">
            <svg className="w-4 h-4 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span>Needs attention · {needsAttentionItems.length}</span>
          </div>

          <div className="mt-3 divide-y divide-[#FFE4E6]">
            {needsAttentionItems.map((item) => (
              <div key={item.id} className="py-2.5 first:pt-0 last:pb-0 flex items-center justify-between text-sm">
                <div className="text-slate-800">{item.text}</div>
                <button
                  type="button"
                  onClick={item.onAction}
                  className="ml-4 px-3 py-1 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm flex-shrink-0"
                >
                  {item.actionLabel}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Two Cards: Users and Partner Program */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card 1: Users */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Users</h2>
              <button
                type="button"
                onClick={() => navigate(adminDashboardTabHref('users'))}
                className="text-xs font-semibold text-indigo-900 hover:text-indigo-700 inline-flex items-center space-x-1"
              >
                <span>Manage users</span>
                <span>→</span>
              </button>
            </div>

            <div className="mt-4 flex items-baseline space-x-2">
              <span className="text-4xl font-extrabold text-slate-900">{totalUsers}</span>
              <span className="text-sm font-medium text-slate-500">total accounts</span>
            </div>

            {/* Segmented Progress Bar */}
            <div className="mt-6 h-2 w-full bg-slate-100 rounded-full overflow-hidden flex">
              <div style={{ width: `${activePct}%` }} className="bg-[#10B981] h-full" title={`Active: ${activeUsers}`}></div>
              <div style={{ width: `${inactivePct}%` }} className="bg-[#94A3B8] h-full" title={`Inactive: ${inactiveCount}`}></div>
            </div>

            {/* Legend underneath */}
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium text-slate-700">
              <div className="flex items-center space-x-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]"></span>
                <span>Active</span>
                <span className="font-bold text-slate-900">{activeUsers}</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#94A3B8]"></span>
                <span>Inactive</span>
                <span className="font-bold text-slate-900">{inactiveCount}</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#DC2626]"></span>
                <span>Locked</span>
                <span className="font-bold text-slate-900">{lockedUsers}</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#0F172A]"></span>
                <span>Admins</span>
                <span className="font-bold text-slate-900">{adminUsers}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Partner Program */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Partner program</h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/admin?tab=insurers&tokens=1')}
                  className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center space-x-1"
                >
                  <span>Token tiers ⚙️</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate(adminDashboardTabHref('insurers'))}
                  className="text-xs font-semibold text-indigo-900 hover:text-indigo-700 inline-flex items-center space-x-1"
                >
                  <span>Insurers</span>
                  <span>→</span>
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                <div className="text-xs text-slate-500 font-medium">Active insurer orgs</div>
                <div className="mt-1 text-2xl font-extrabold text-slate-900">
                  {partnerMetrics?.active_insurer_orgs ?? 1}
                </div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                <div className="text-xs text-slate-500 font-medium">Pending approval</div>
                <div className="mt-1 text-2xl font-extrabold text-slate-900">
                  {partnerMetrics?.pending_insurer_orgs ?? 0}
                </div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                <div className="text-xs text-slate-500 font-medium">Outstanding ledger</div>
                <div className="mt-1 text-2xl font-extrabold text-[#DC2626]">
                  {outstandingDollars}
                </div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                <div className="text-xs text-slate-500 font-medium">Credits granted · MTD</div>
                <div className="mt-1 text-2xl font-extrabold text-slate-900">
                  {partnerMetrics?.credits_granted_mtd ?? 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Three Cards: New Subscriptions, User Growth, Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Card 1: New Subscriptions */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">New subscriptions</h2>
              <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                Month to date
              </span>
            </div>

            <div className="mt-4 space-y-3 divide-y divide-slate-100 text-sm">
              <div className="pt-2 flex justify-between items-center text-slate-700">
                <span>{tokenTitles.simple || 'Prepared'}</span>
                <span className="font-bold text-slate-900">{partnerMetrics?.simple_tokens_mtd ?? 0}</span>
              </div>
              <div className="pt-2 flex justify-between items-center text-slate-700">
                <span>{tokenTitles.gold || 'Prepared+'}</span>
                <span className="font-bold text-slate-900">{partnerMetrics?.gold_tokens_mtd ?? 0}</span>
              </div>
              <div className="pt-2 flex justify-between items-center text-slate-700">
                <span>{tokenTitles.premium || 'Executive'}</span>
                <span className="font-bold text-slate-900">{partnerMetrics?.premium_tokens_mtd ?? 0}</span>
              </div>
              <div className="pt-2 flex justify-between items-center text-slate-700">
                <span>Audit requests</span>
                <span className="font-bold text-slate-900">{partnerMetrics?.audit_requests_mtd ?? 0}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-3 border-t border-slate-100 text-xs text-slate-500">
            No new subscriptions yet this month.{' '}
            <button
              type="button"
              onClick={() => navigate(adminDashboardTabHref('pricing'))}
              className="text-indigo-900 font-semibold hover:underline"
            >
              Review pricing
            </button>
          </div>
        </div>

        {/* Card 2: User Growth */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">User growth</h2>

            {/* Growth Numbers */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="bg-slate-50 p-2.5 rounded-lg text-center border border-slate-100">
                <div className="text-xl font-extrabold text-slate-900">{stats.new_users_today}</div>
                <div className="text-[11px] text-slate-500">Today</div>
              </div>
              <div className="bg-slate-50 p-2.5 rounded-lg text-center border border-slate-100">
                <div className="text-xl font-extrabold text-slate-900">{stats.new_users_this_week}</div>
                <div className="text-[11px] text-slate-500">This week</div>
              </div>
              <div className="bg-slate-50 p-2.5 rounded-lg text-center border border-slate-100">
                <div className="text-xl font-extrabold text-slate-900">{stats.new_users_this_month}</div>
                <div className="text-[11px] text-slate-500">This month</div>
              </div>
            </div>

            {/* Recent Sign-ins */}
            <div className="mt-5">
              <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                Recent Sign-ins
              </div>
              <div className="mt-2 space-y-2 text-xs">
                {recentUsers.length === 0 ? (
                  <p className="text-slate-400">No recent sign-ins recorded</p>
                ) : (
                  recentUsers.map((u) => (
                    <div key={u.id} className="flex items-center justify-between py-1">
                      <span className="font-medium text-slate-800">
                        {u.full_name || u.email}
                      </span>
                      <span className="text-slate-400">{formatLastLogin(u.last_login)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Quick Actions */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Quick actions</h2>

            <div className="mt-4 space-y-2.5">
              <button
                type="button"
                onClick={() => setShowUserForm(true)}
                className="w-full py-2.5 px-4 bg-[#991B1B] hover:bg-[#7F1D1D] text-white rounded-lg text-xs font-bold transition-colors flex items-center justify-center space-x-2 shadow-sm"
              >
                <span>+</span>
                <span>Add user</span>
              </button>

              <button
                type="button"
                onClick={() => setShowDemoUserForm(true)}
                className="w-full py-2.5 px-4 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center space-x-2 shadow-sm"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>Create demo user</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/admin?tab=insurers&create=1')}
                className="w-full py-2.5 px-4 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center space-x-2 shadow-sm"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Invite insurer</span>
              </button>

              <button
                type="button"
                onClick={() => navigate(adminDashboardTabHref('templates'))}
                className="w-full py-2.5 px-4 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center space-x-2 shadow-sm"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>New tabletop template</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/admin?tab=insurers&tokens=1')}
                className="w-full py-2.5 px-4 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center space-x-2 shadow-sm"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Partner token names & tiers</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Action Modals */}
      {showUserForm && (
        <UserForm
          onClose={() => setShowUserForm(false)}
          onSuccess={() => {
            setShowUserForm(false);
            handleRefresh();
          }}
        />
      )}

      {showDemoUserForm && (
        <DemoUserForm
          onClose={() => setShowDemoUserForm(false)}
          onSuccess={() => {
            setShowDemoUserForm(false);
            handleRefresh();
          }}
        />
      )}
    </div>
  );
};

export default AdminOverview;
